import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';
import { LoggerService } from './service/utils/logger.service';

export class CustomReuseStrategy implements RouteReuseStrategy {
  private storedRoutes = new Map<string, DetachedRouteHandle>();

  /** Clear all cached routes (e.g. after logout/login). */
  clear(): void {
    this.storedRoutes.clear();
  }

  private getRouteKey(route: ActivatedRouteSnapshot): string {
    // Use the full config path from root to avoid collisions (especially for '' child paths).
    // Example keys: 'dashboard/_', 'admin/_', 'question-pool-study/:questionId/:init?'
    const parts = route.pathFromRoot
      .map(r => r.routeConfig?.path)
      .filter((p): p is string => p !== undefined)
      .map(p => (p === '' ? '_' : p));
    return parts.join('/');
  }

  private isCacheable(route: ActivatedRouteSnapshot): boolean {
    const path = route.routeConfig?.path ?? '';
    // Never cache auth routes.
    if (path === 'login' || path === 'logout') return false;
    // Avoid caching wildcard/404.
    if (path === '**') return false;
    return true;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // Decide if the route should be detached (stored for reuse).
    return this.isCacheable(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    // Store the detached route handle.
    if (!this.isCacheable(route)) return;
    const key = this.getRouteKey(route);
    this.storedRoutes.set(key, handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    // Check if the stored route should be reattached, and the path doesn't contain 'init'.

    if (route.url.length > 2 && route.url[2].path == 'init') {
      return false;
    }

    if (!this.isCacheable(route)) return false;
    const key = this.getRouteKey(route);
    LoggerService.log("shouldAttach: "  + !!this.storedRoutes.get(key));
    return !!this.storedRoutes.get(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    // Retrieve the stored route handle or return null if it doesn't exist.
    if (route.url.length > 2 && route.url[2].path == 'init') {
      return null;
    }
    if (!this.isCacheable(route)) return null;
    const key = this.getRouteKey(route);
    return this.storedRoutes.get(key) || null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
    const futurePath = future.routeConfig?.path || '';
    const currentPath = current.routeConfig?.path || '';

    // If the path contains 'init', do not reuse the route.
    //if (futurePath.includes('init') /* || currentPath.includes('init')*/) {
//      return false;
    //}

    if (future.url.length > 2 && future.url[2].path == 'init') {
      return false;
    }

    // Otherwise, reuse the route if the route configuration is the same.
    LoggerService.log("shouldReuse: ", future.routeConfig === current.routeConfig);
    return future.routeConfig === current.routeConfig;
  }
}
